import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { signIn, signOut } from 'aws-amplify/auth';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { InputComponent } from '../shared/input';
import { ButtonComponent } from '../shared/button';
import { LocaleService } from '../../services/locale';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    CommonModule,
    InputComponent,
    ButtonComponent
  ],
  template: `
    <div class="flex flex-col gap-1 mb-3 sm:mb-4">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold futura text-[#043B22]">
        {{ locale.login.heading }}
      </h1>
      <p class="text-[12px] sm:text-[12px] text-[#616161]">
        {{ locale.login.sub_dont_have_account }}
        <a
          routerLink="/register"
          class="text-[#043B22] font-medium hover:text-[#043B22]/80 transition-colors underline underline-offset-2"
          >{{ locale.login.sub_dont_have_account_s }}</a
        >
      </p>
    </div>
    <form class="flex flex-col gap-2 sm:gap-2.5" [formGroup]="form" (submit)="submit()">
      <app-input
        label="{{ locale.common.lbl_email }}"
        type="email"
        [keyUp]="reset"
        placeholder="{{ locale.common.placeholder_email }}"
        [control]="form.controls['email']"
      ></app-input>
      <app-input
        label="{{ locale.common.lbl_password }}"
        type="password"
        [keyUp]="reset"
        placeholder="{{ locale.common.placeholder_password }}"
        [control]="form.controls['password']"
      ></app-input>
      <div class="flex flex-col sm:flex-row sm:justify-between w-full gap-1.5 sm:gap-0 my-0.5">
        <div class="flex gap-1.5 items-center cursor-pointer">
          <input
            id="rem"
            type="checkbox"
            class="w-3.5 h-3.5 accent-[#043B22] cursor-pointer rounded"
          />
          <label for="rem" class="cursor-pointer text-[12px] sm:text-[12px] text-[#616161]">
            {{ locale.login.remember_password }}
          </label>
        </div>
        <a
          routerLink="/forgot"
          class="text-[#043B22] text-[12px] sm:text-[12px] font-medium cursor-pointer hover:text-[#043B22]/80 transition-colors"
          >{{ locale.common.text_forget_password }}?
        </a>
      </div>
      <div *ngIf="error.length" class="text-red-500 text-[12px] sm:text-[12px] bg-red-50 p-2 rounded">
        {{ error }}
      </div>
      <div class="mt-1">
        <app-button
          [disabled]="form.invalid"
          text="{{ locale.login.button_login }}"
        ></app-button>
      </div>
    </form>
  `
})
export class LoginComponent {
  private router: Router = inject(Router);
  private spinner = inject(NgxSpinnerService);
  locale = inject(LocaleService).locale();

  error = '';

  form = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]),
    password: new FormControl('', Validators.required)
  });

  constructor(private route: ActivatedRoute) {
    this.route.queryParamMap.subscribe(async params => {
      const lo = params.get('lo');
      if (lo && lo === 'y') {
        window.location.href = '/login';
      }
    })
  }

  submit = async () => {
    try {
      this.spinner.show();

      await signOut();
      localStorage.clear();

      const credentials = {
        username: this.form.controls.email.value ?? '',
        password: this.form.controls.password.value ?? ''
      };

      const user = await signIn({ ...credentials, options: { authFlowType: 'USER_PASSWORD_AUTH' } });

      this.spinner.hide();

      if (user.isSignedIn) {
        this.router.navigateByUrl('/dashboard/calculations');
      }
    } catch (error) {
      this.spinner.hide();
      this.error = (error as any).message;
    }
  };

  reset = () => {
    this.error = '';
  };
}
